<template>
    <AppForm>
        <FormRow v-if="resolvedAttestation">
            <DataTable>
                <TableRow>
                    <TableCell
                        ><strong>{{ $t("Certification type") }}</strong></TableCell
                    >
                    <TableCell>
                        <BadgeComponent
                            :text="renderAttestationType(resolvedAttestation.type)"
                            :style-type="BadgeStyle.BORDER"
                            :shape="BadgeShape.BADGE"
                            :color="ColorAccent.NEUTRAL"
                        />
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell
                        ><strong>{{ $t("Certificator") }} ({{ $t("ID") }})</strong></TableCell
                    >
                    <TableCell>{{ resolvedAttestation.attestator || "-" }}</TableCell>
                </TableRow>
                <TableRow v-if="resolvedAttestation.attestatorName">
                    <TableCell
                        ><strong>{{ $t("Attestator") }} ({{ $t("Name") }})</strong></TableCell
                    >
                    <TableCell>{{ resolvedAttestation.attestatorName || "-" }}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell
                        ><strong>{{ $t("Subject") }} ({{ $t("ID") }})</strong></TableCell
                    >
                    <TableCell>{{ resolvedSubject || "-" }}</TableCell>
                </TableRow>
                <TableRow v-if="resolvedSubjectName">
                    <TableCell
                        ><strong>{{ $t("Subject") }} ({{ $t("Name") }})</strong></TableCell
                    >
                    <TableCell>{{ resolvedSubjectName || "-" }}</TableCell>
                </TableRow>
                <TableRow>
                    <TableCell
                        ><strong>{{ $t("Expiration date") }}</strong></TableCell
                    >
                    <TableCell>{{ renderDateAndTime(resolvedAttestation.expiration, $t) }}</TableCell>
                </TableRow>
            </DataTable>
        </FormRow>
        <FormRow>
            <FormField
                :label="$t('Attributes')"
                :description="$t('The identity claims the attestator signed, such as the legal name or the country of the participant')"
            >
                <DataTable :column-headers="[$t('Name'), $t('Value')]">
                    <TableRow v-for="(attributes, index) in resolvedAttestation?.attributes" :key="index">
                        <TableCell>{{ attributes.name }}</TableCell>
                        <TableCell>{{ attributes.value }}</TableCell>
                    </TableRow>
                </DataTable>
            </FormField>
        </FormRow>
        <FormRow>
            <CodeBlock :language="'raw'" :code="resolvedAttestation?.raw" :name="$t('Raw attestation')"></CodeBlock>
        </FormRow>
    </AppForm>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { useVModel } from "../../utils/v-model";
import { DataSpaceKnownParticipantAttestation } from "@/api/definitions";
import { renderDateAndTime } from "@/utils/time-utils";
import { BadgeShape, BadgeStyle } from "@/utils/enums/badges";
import { ColorAccent } from "@/utils/enums/colors";

import CodeBlock from "../utils/CodeBlock.vue";
import BadgeComponent from "../ui/BadgeComponent.vue";
import AppForm from "../forms/form/AppForm.vue";
import FormRow from "../forms/form/FormRow.vue";
import FormField from "../forms/form/fields/FormField.vue";
import DataTable from "../tables/data-table/DataTable.vue";
import TableRow from "../tables/data-table/TableRow.vue";
import TableCell from "../tables/data-table/TableCell.vue";

export default defineComponent({
    components: {
        CodeBlock,
        BadgeComponent,
        AppForm,
        FormRow,
        FormField,
        DataTable,
        TableRow,
        TableCell,
    },
    name: "AttestationViewModalContent",
    emits: ["update:display"],
    props: {
        display: Boolean,
        attestation: Object as PropType<DataSpaceKnownParticipantAttestation>,
        subject: String,
        subjectName: String,
        sharedData: {
            type: Object,
            default: () => ({}),
        },
    },
    setup(props) {
        return {
            displayStatus: useVModel(props, "display"),
        };
    },
    data() {
        return {
            BadgeStyle: BadgeStyle,
            BadgeShape: BadgeShape,
            ColorAccent: ColorAccent,
        };
    },
    methods: {
        close: function () {
            this.$closeModal();
        },

        renderAttestationType: function (type: string) {
            switch (type) {
                case "x509":
                    return this.$t("X.509 Signed Certificate");
                case "did-jwt":
                    return this.$t("JWT DID Attestation");
                default:
                    return "-";
            }
        },

        renderDateAndTime: renderDateAndTime,
    },
    mounted: function () {
        if (this.display) {
            this.$autoFocus();
        }
    },
    beforeUnmount: function () {},

    computed: {
        resolvedAttestation(): DataSpaceKnownParticipantAttestation | undefined {
            return this.attestation ?? this.sharedData?.attestation;
        },
        resolvedSubject(): string | undefined {
            return this.subject ?? this.sharedData?.subject;
        },
        resolvedSubjectName(): string | undefined {
            return this.subjectName ?? this.sharedData?.subjectName;
        },
    },
});
</script>
